import React from 'react';
import { X, Download, TrendingUp, TrendingDown } from 'lucide-react';

interface DetailedReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    reportType: string;
    dateRange: string;
    reportData: any[];
}

const money = (value: number) =>
    `$${Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const DetailedReportModal: React.FC<DetailedReportModalProps> = ({
                                                                     isOpen,
                                                                     onClose,
                                                                     reportType,
                                                                     dateRange,
                                                                     reportData
                                                                 }) => {
    if (!isOpen) return null;

    const renderRevenueReport = () => (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
                <thead className="bg-gray-50">
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Gross Sales</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Discount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Tax</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Tips</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Net Total</th>
                </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                {reportData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{row.date}</td>
                        <td className="px-4 py-3 text-sm">{money(row.grossSales)}</td>
                        <td className="px-4 py-3 text-sm text-red-600">-{money(row.discount)}</td>
                        <td className="px-4 py-3 text-sm">{money(row.tax)}</td>
                        <td className="px-4 py-3 text-sm text-green-600">{money(row.tips)}</td>
                        <td className="px-4 py-3 text-sm font-semibold">{money(row.netTotal)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
    const renderServicesReport = () => (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
                <thead className="bg-gray-50">
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Bookings</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Revenue</th>
                </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                {reportData.map((row, idx) => (
                    <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium">{row.serviceName}</td>
                        <td className="px-4 py-3 text-sm">{row.bookings}</td>
                        <td className="px-4 py-3 text-sm font-semibold">{money(row.revenue)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
    const renderProductsReport = () => (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
                <thead className="bg-gray-50">
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Units Sold</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Revenue</th>
                </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                {reportData.map((row, idx) => (
                    <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium">{row.productName}</td>
                        <td className="px-4 py-3 text-sm">{row.unitsSold}</td>
                        <td className="px-4 py-3 text-sm font-semibold">{money(row.revenue)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
    const renderStaffReport = () => (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
                <thead className="bg-gray-50">
                <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Staff</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Treatments</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Service Revenue</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Product Revenue</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Total Revenue</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Tips</th>
                </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                {reportData.map((row, idx) => (
                    <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium">{row.staffName}</td>
                        <td className="px-4 py-3 text-sm">{row.treatments}</td>
                        <td className="px-4 py-3 text-sm">{money(row.serviceRevenue)}</td>
                        <td className="px-4 py-3 text-sm">{money(row.productRevenue)}</td>
                        <td className="px-4 py-3 text-sm font-semibold">{money(row.totalRevenue)}</td>
                        <td className="px-4 py-3 text-sm text-green-600">{money(row.tipsEarned)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
    const getReportTitle = () => {
        switch (reportType) {
            case 'revenue':
                return 'Sales Report';
            case 'services':
                return 'Services Performance Report';
            case 'products':
                return 'Products Performance Report';
            case 'staff':
                return 'Staff Performance Report';
            default:
                return 'Report';
        }
    };

    const renderContent = () => {
        switch (reportType) {
            case 'revenue':
                return renderRevenueReport();
            case 'services':
                return renderServicesReport();
            case 'products':
                return renderProductsReport();
            case 'staff':
                return renderStaffReport();
            default:
                return null;
        }
    };

    const exportCSV = () => {
        if (!reportData || reportData.length === 0) return;

        let headers: string[] = [];
        let rows: any[] = [];

        switch (reportType) {

            case "revenue":
                headers = ["Date", "Gross Sales", "Discount", "Tax", "Tips", "Net Total"];
                rows = reportData.map(r => [
                    r.date,
                    r.grossSales,
                    r.discount,
                    r.tax,
                    r.tips,
                    r.netTotal
                ]);
                break;

            case "services":
                headers = ["Service", "Bookings", "Revenue"];
                rows = reportData.map(r => [
                    r.serviceName,
                    r.bookings,
                    r.revenue
                ]);
                break;

            case "products":
                headers = ["Product", "Units Sold", "Revenue"];
                rows = reportData.map(r => [
                    r.productName,
                    r.unitsSold,
                    r.revenue
                ]);
                break;

            case "staff":
                headers = [
                    "Staff",
                    "Treatments",
                    "Service Revenue",
                    "Product Revenue",
                    "Total Revenue",
                    "Tips"
                ];
                rows = reportData.map(r => [
                    r.staffName,
                    r.treatments,
                    r.serviceRevenue,
                    r.productRevenue,
                    r.totalRevenue,
                    r.tipsEarned
                ]);
                break;

            default:
                return;
        }

        const csvContent =
            [headers, ...rows]
                .map(e => e.join(","))
                .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", `${reportType}-report.csv`);
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900">{getReportTitle()}</h2>
                        <p className="text-sm text-gray-600 mt-1">Period: {dateRange}</p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={exportCSV}
                            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                        >
                            <Download className="w-4 h-4" />
                            <span>Export</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 p-2"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default DetailedReportModal;
